import { Fragment, useMemo } from 'react';
import category from '~/assets/icons/category.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { PanelControls } from '~/components/ui/PanelControls';
import { ShopContainer } from '~/components/ui/Shop';
import { ShopBalance } from '~/components/ui/ShopBalance';
import { ShopHead } from '~/components/ui/ShopHead';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	Currency,
	InputAction,
	InputKey,
	LootBuyerType,
	WalletType,
} from '~/shared';
import { LootList } from '../../components/LootList';
import { SectionHeading } from '../../components/SectionHeading';
import { UpdatePriceTimer } from '../../components/UpdatePriceTimer';
import { useSell } from '../../hooks';
import { lootBuyersLogo } from './LootBuyers.model';
import {
	StyledControlsContainer,
	StyledRightContainer,
	StyledSellAllButtonContent,
} from './LootBuyers.styled';

const LootBuyers = () => {
	const items = useObservable(rage.lootBuyers.getSellItems());
	const ownedItems = useObservable(rage.lootBuyers.getOwnedItems());
	const canSellFish = Object.keys(ownedItems).length > 0;
	const totalPrice = useMemo(
		() =>
			Object.entries(ownedItems).reduce(
				(acc, [itemId, count]) =>
					acc +
					(items.find((sellItem) => sellItem.id === itemId)?.price ?? 0) *
						count,
				0,
			),
		[ownedItems, items],
	);
	const { handleSellAll } = useSell();
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const currentLootBuyerType = useObservable(
		rage.lootBuyers.getCurrentLootBuyerType(),
	);

	const leftControls = [
		{
			text: 'Продать весь товар',
			keys: [InputKey.R],
			onClick: () => {
				if (canSellFish) {
					handleSellAll(totalPrice);
				}
			},
		},
	];

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => rage.lootBuyers.exitMenu(),
		},
	];

	useInputAction(InputAction.LootBuyersSellAll, () => {
		if (canSellFish && !modalState) {
			handleSellAll(totalPrice);
		}
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		if (!modalState) {
			rage.lootBuyers.exitMenu();
		}
	});

	return (
		<Fragment>
			<StyledRightContainer>
				<ShopBalance walletTypes={[WalletType.Cash]} />
				<CloseButton
					height={ButtonHeight.Height40}
					color={ButtonColors.Black1}
					iconSize={20}
					onClick={() => rage.lootBuyers.exitMenu()}
				/>
			</StyledRightContainer>
			<ShopContainer>
				<ShopHead
					img={lootBuyersLogo[currentLootBuyerType ?? LootBuyerType.FISH]}
					text={''}
					height={30}
				/>
				<UpdatePriceTimer style={{ marginTop: 30 }} />
				<SectionHeading
					icon={category}
					name={'Все товары'}
					style={{ marginTop: 30 }}
				/>
				<LootList style={{ marginTop: 20 }} />
				<Button
					color={ButtonColors.Buy}
					height={ButtonHeight.Height50}
					style={{ marginTop: 30 }}
					disabled={!canSellFish || totalPrice === 0}
					onClick={() => handleSellAll(totalPrice)}
				>
					{canSellFish && totalPrice > 0 ? (
						<StyledSellAllButtonContent>
							<Text>Продать весь товар</Text>
							<CurrencyIcon icon={rubleIcon} size="md" asMask />
							<Text style={{ fontWeight: 500 }}>
								{totalPrice.toLocaleString('ru-RU')}
							</Text>
						</StyledSellAllButtonContent>
					) : (
						<Text>У вас нет товаров на продажу</Text>
					)}
				</Button>
			</ShopContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} />
				<PanelControls controls={rightControls} />
			</StyledControlsContainer>
		</Fragment>
	);
};

export { LootBuyers };
